import { getErrorMessage } from "@/lib/api/api.client";
import {
  getCurrentRoleIdsForGroup,
  getCurrentUserIdsForGroup,
  useCreateAuthGroup,
  useUpdateAuthGroup,
  type FronteggGroup,
  type FronteggGroupUser,
} from "@/lib/auth/groups/groups.api";
import AdaptiveDialog, {
  AdaptiveDialogAction,
  AdaptiveDialogCancel,
  AdaptiveDialogClose,
  AdaptiveDialogContent,
  AdaptiveDialogFooter,
  AdaptiveDialogHeader,
  AdaptiveDialogTitle,
  AdaptiveDialogTrigger,
  useAdaptiveDialog,
} from "@/lib/components/ui/adaptive-dialog/adaptive-dialog";
import {
  ColorField,
  ColorFieldHexInput,
  ColorFieldPreview,
  ColorFieldSwatches,
} from "@/lib/components/ui/color-field/color-field";
import Field, { FieldError, FieldLabel } from "@/lib/components/ui/field/field";
import Input from "@/lib/components/ui/input/input";
import {
  RolesSelect,
  RolesSelectContent,
  RolesSelectTrigger,
  RolesSelectValue,
} from "@/lib/auth/roles/components/roles-select";
import Spinner from "@/lib/components/ui/spinner";
import Textarea from "@/lib/components/ui/textarea/textarea";
import {
  UsersSelect,
  UsersSelectContent,
  UsersSelectTrigger,
  UsersSelectValue,
} from "@/lib/auth/users/components/users-select";
import { formOptions, isFieldInvalid, useForm } from "@soulbyte/core";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  createGroupFormSchema,
  getGroupFormDefaults,
  mergeGroupColorChoices,
  type GroupFormValues,
} from "./groups.form";

export const GroupsFormDialog = AdaptiveDialog;
export const GroupsFormDialogTrigger = AdaptiveDialogTrigger;

export type GroupsFormDialogContentProps = {
  mode: "create" | "edit";
  group?: FronteggGroup | null;
  onCreated?: (groupId: string) => void;
};

function formatGroupUserLabel(user: FronteggGroupUser): string {
  const name = typeof user.name === "string" ? user.name.trim() : "";
  const email = typeof user.email === "string" ? user.email.trim() : "";
  if (!name || name === email) {
    return email || user.id;
  }
  if (!email) {
    return name;
  }
  return `${name} (${email})`;
}

function getSelectedUserItems(group?: FronteggGroup | null) {
  if (!Array.isArray(group?.users)) {
    return [];
  }
  return group.users.map(function toSelectedUserItem(user) {
    return {
      id: user.id,
      label: formatGroupUserLabel(user),
    };
  });
}

export function GroupsFormDialogContent({
  mode,
  group,
  onCreated,
}: GroupsFormDialogContentProps) {
  const { t } = useTranslation();
  const { onOpenChange } = useAdaptiveDialog();

  const schema = createGroupFormSchema(t);
  const createGroup = useCreateAuthGroup();
  const updateGroup = useUpdateAuthGroup();

  const preserveRoleIds =
    mode === "edit" && group ? getCurrentRoleIdsForGroup(group) : [];
  const selectedUserItems = getSelectedUserItems(group);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const colorChoices = mergeGroupColorChoices(
    typeof group?.color === "string" ? group.color : null,
  );
  const isPending = createGroup.isPending || updateGroup.isPending;

  async function handleSubmit(values: GroupFormValues) {
    try {
      if (mode === "create") {
        const groupId = await createGroup.mutateAsync({
          name: values.name.trim(),
          color: values.color,
          roleIds: values.roleIds,
          userIds: values.userIds,
          description: values.description.trim(),
        });
        toast.success(t("settings_groups_toast_created"));
        onCreated?.(groupId);
        onOpenChange?.(false);
        return;
      }

      if (!group?.id) {
        toast.error(t("settings_groups_toast_error"));
        return;
      }

      await updateGroup.mutateAsync({
        name: values.name.trim(),
        color: values.color,
        groupId: group.id,
        roleIds: values.roleIds,
        userIds: values.userIds,
        description: values.description.trim(),
        currentRoleIds: getCurrentRoleIdsForGroup(group),
        currentUserIds: getCurrentUserIdsForGroup(group),
      });
      toast.success(t("settings_groups_toast_updated"));
      onOpenChange?.(false);
    } catch (error) {
      toast.error(getErrorMessage(error) || t("settings_groups_toast_error"));
    }
  }

  const form = useForm({
    ...formOptions,
    onSubmit: ({ value }) => handleSubmit(value),
    validators: { onSubmit: schema },
    defaultValues: getGroupFormDefaults(group),
  });

  return (
    <AdaptiveDialogContent
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        nameInputRef.current?.focus();
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
      >
        <AdaptiveDialogClose type="button" />
        <AdaptiveDialogHeader>
          <AdaptiveDialogTitle>
            {mode === "create"
              ? t("settings_groups_form_title_create")
              : t("settings_groups_form_title_edit")}
          </AdaptiveDialogTitle>
        </AdaptiveDialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <form.Field
            name="name"
            children={(field) => {
              const isInvalid = isFieldInvalid(field);
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor="settings-group-name">
                    {t("settings_groups_form_name")}
                  </FieldLabel>
                  <Input
                    id="settings-group-name"
                    ref={nameInputRef}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    disabled={isPending}
                    onChange={(event) => field.handleChange(event.target.value)}
                    placeholder={t("settings_groups_form_name_placeholder")}
                    aria-invalid={isInvalid}
                  />
                  {isInvalid ? (
                    <FieldError errors={field.state.meta.errors} />
                  ) : null}
                </Field>
              );
            }}
          />

          <form.Field
            name="description"
            children={(field) => {
              const isInvalid = isFieldInvalid(field);
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor="settings-group-description">
                    {t("settings_groups_form_description")}
                  </FieldLabel>
                  <Textarea
                    id="settings-group-description"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    disabled={isPending}
                    onChange={(event) => field.handleChange(event.target.value)}
                    placeholder={t(
                      "settings_groups_form_description_placeholder",
                    )}
                    aria-invalid={isInvalid}
                  />
                  {isInvalid ? (
                    <FieldError errors={field.state.meta.errors} />
                  ) : null}
                </Field>
              );
            }}
          />

          <form.Field
            name="color"
            children={(field) => {
              const isInvalid = isFieldInvalid(field);
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel>{t("settings_groups_form_color")}</FieldLabel>
                  <ColorField
                    value={field.state.value}
                    colors={colorChoices}
                    disabled={isPending}
                    onValueChange={field.handleChange}
                  >
                    <ColorFieldSwatches
                      groupAriaLabel={t("settings_groups_form_color")}
                      getSwatchAriaLabel={(hex) =>
                        t("settings_groups_color_swatch", { color: hex })
                      }
                    />
                    <div className="flex items-center gap-3">
                      <ColorFieldPreview
                        pickerAriaLabel={t("settings_groups_color_picker")}
                      />
                      <ColorFieldHexInput
                        onBlur={field.handleBlur}
                        disabled={isPending}
                        aria-label={t("settings_groups_color_hex")}
                        aria-invalid={isInvalid}
                      />
                    </div>
                  </ColorField>
                  {isInvalid ? (
                    <FieldError errors={field.state.meta.errors} />
                  ) : null}
                </Field>
              );
            }}
          />

          <form.Field
            name="roleIds"
            children={(field) => {
              const isInvalid = isFieldInvalid(field);
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel>{t("settings_groups_form_roles")}</FieldLabel>
                  <RolesSelect
                    value={field.state.value}
                    disabled={isPending}
                    onValueChange={field.handleChange}
                    preserveRoleIds={preserveRoleIds}
                  >
                    <RolesSelectTrigger />
                    <RolesSelectValue />
                    <RolesSelectContent />
                  </RolesSelect>
                  {isInvalid ? (
                    <FieldError errors={field.state.meta.errors} />
                  ) : null}
                </Field>
              );
            }}
          />

          <form.Field
            name="userIds"
            children={(field) => {
              const isInvalid = isFieldInvalid(field);
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel>{t("settings_groups_form_users")}</FieldLabel>
                  <UsersSelect
                    value={field.state.value}
                    disabled={isPending}
                    onValueChange={field.handleChange}
                    selectedItems={selectedUserItems}
                  >
                    <UsersSelectTrigger />
                    <UsersSelectValue />
                    <UsersSelectContent />
                  </UsersSelect>
                  {isInvalid ? (
                    <FieldError errors={field.state.meta.errors} />
                  ) : null}
                </Field>
              );
            }}
          />
        </div>

        <AdaptiveDialogFooter>
          <AdaptiveDialogClose asChild>
            <AdaptiveDialogCancel type="button" disabled={isPending}>
              {t("common_cancel")}
            </AdaptiveDialogCancel>
          </AdaptiveDialogClose>
          <AdaptiveDialogAction type="submit" disabled={isPending}>
            {isPending ? <Spinner /> : null}
            {t("common_save")}
          </AdaptiveDialogAction>
        </AdaptiveDialogFooter>
      </form>
    </AdaptiveDialogContent>
  );
}
